import React, { useEffect, useRef, useState } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Toaster } from '../components/ui/toaster';
import { useToast } from '../hooks/use-toast';
import { 
  Mail, Phone, MapPin, Send, ArrowRight, 
  MessageSquare, Clock, CheckCircle, Sparkles
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ContactPage = () => {
  const pageRef = useRef(null);
  const { toast } = useToast();
  const [companyInfo, setCompanyInfo] = useState({
    phone: '+91 8500820088',
    email: 'info@uzvis.com',
    location: 'Vijayawada, India'
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/company-info`)
      .then(res => res.json())
      .then(data => setCompanyInfo(data))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = pageRef.current?.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-scale');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const res = await fetch(`${API_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast({
          title: "Message Sent!",
          description: "We'll get back to you within 24 hours.",
        });
        setFormData({ name: '', email: '', phone: '', message: '' });
      } else {
        throw new Error('Failed to send');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const contactInfo = [
    { icon: Phone, label: 'Phone', value: companyInfo.phone, href: `tel:${companyInfo.phone}` },
    { icon: Mail, label: 'Email', value: companyInfo.email, href: `mailto:${companyInfo.email}` },
    { icon: MapPin, label: 'Location', value: companyInfo.location, href: '#' },
  ];

  const steps = [
    { icon: MessageSquare, title: 'Share Requirements', description: 'Tell us about your project' },
    { icon: Clock, title: 'Quick Response', description: 'Get a response within 24 hours' },
    { icon: CheckCircle, title: 'Start Building', description: 'Begin your project journey' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-orange-50/30" ref={pageRef}>
      <Header />
      <main className="pt-20">
        {/* Hero Section */}
        <section className="relative py-20 lg:py-24 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 overflow-hidden">
            <div className="hero-orb hero-orb-1 opacity-40"></div>
            <div className="hero-orb hero-orb-2 opacity-30"></div>
            <div className="absolute inset-0 dot-grid opacity-20"></div>
          </div>

          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="max-w-3xl mx-auto text-center reveal">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full shadow-lg shadow-orange-500/5 border border-orange-100/50 mb-6">
                <Sparkles className="w-4 h-4 text-[#ff6b35]" />
                <span className="text-sm font-medium text-gray-700">Let's Connect</span>
              </div>
              
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6" data-testid="contact-hero-title">
                Get In
                <span className="bg-gradient-to-r from-[#ff6b35] via-[#ff8f5c] to-[#ff6b35] bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient-x"> Touch</span>
              </h1>

              <p className="text-lg text-gray-600 leading-relaxed max-w-2xl mx-auto">
                Have a project in mind? Let's discuss how we can help transform your business with our expertise.
              </p>
            </div>
          </div>
        </section>

        {/* Contact Form & Info Section */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-5 gap-12">
              {/* Contact Info */}
              <div className="lg:col-span-2 reveal-left">
                <div className="sticky top-24">
                  <h2 className="text-2xl font-bold text-gray-900 mb-6">Contact Information</h2>
                  
                  <div className="space-y-4 mb-10">
                    {contactInfo.map((info, i) => (
                      <a 
                        key={info.label}
                        href={info.href}
                        className="flex items-center gap-4 p-4 bg-gray-50 rounded-2xl hover:bg-orange-50 hover:border-[#ff6b35]/30 border border-transparent transition-all duration-300 group"
                        data-testid={`contact-info-${info.label.toLowerCase()}`}
                      >
                        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                          <info.icon className="w-5 h-5 text-[#ff6b35]" />
                        </div>
                        <div>
                          <div className="text-sm text-gray-500">{info.label}</div>
                          <div className="font-semibold text-gray-900">{info.value}</div>
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* Process Steps */}
                  <div className="bg-gray-900 rounded-3xl p-6">
                    <h3 className="text-white font-semibold mb-6">How it works</h3>
                    <div className="space-y-4">
                      {steps.map((step, i) => (
                        <div key={step.title} className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-xl bg-[#ff6b35]/20 flex items-center justify-center flex-shrink-0">
                            <step.icon className="w-5 h-5 text-[#ff6b35]" />
                          </div>
                          <div>
                            <div className="text-white font-medium">{step.title}</div>
                            <div className="text-gray-400 text-sm">{step.description}</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div className="lg:col-span-3 reveal-right">
                <div className="bento-card p-8 lg:p-10">
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Send us a message</h2>
                  <p className="text-gray-600 mb-8">Fill out the form below and we'll get back to you shortly.</p>

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Full Name</label>
                        <input
                          type="text"
                          required
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all"
                          placeholder="John Doe"
                          data-testid="contact-input-name"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                        <input
                          type="email"
                          required
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all"
                          placeholder="john@example.com"
                          data-testid="contact-input-email"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                      <input
                        type="tel"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all"
                        placeholder="+91 1234567890"
                        data-testid="contact-input-phone"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                      <textarea
                        required
                        rows={5}
                        value={formData.message}
                        onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:border-[#ff6b35] focus:ring-2 focus:ring-[#ff6b35]/20 outline-none transition-all resize-none"
                        placeholder="Tell us about your project..."
                        data-testid="contact-input-message"
                      />
                    </div>

                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-xl font-semibold shadow-xl shadow-[#ff6b35]/30 hover:shadow-[#ff6b35]/50 hover:scale-[1.01] transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed"
                      data-testid="contact-submit-btn"
                    >
                      {submitting ? (
                        <>
                          <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <Send className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ / Additional Info Section */}
        <section className="py-20 bg-gradient-to-br from-gray-50 to-orange-50/30">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid md:grid-cols-3 gap-6" data-testid="contact-features">
              {[
                { title: 'Fast Response', description: 'We respond to all inquiries within 24 hours', icon: Clock },
                { title: 'Expert Consultation', description: 'Get advice from experienced professionals', icon: MessageSquare },
                { title: 'Global Support', description: 'Available across multiple time zones', icon: MapPin },
              ].map((feature, i) => (
                <div 
                  key={feature.title}
                  className="reveal-scale bento-card text-center"
                  style={{ animationDelay: `${i * 0.1}s` }}
                >
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-orange-50 to-orange-100 flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-7 h-7 text-[#ff6b35]" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">{feature.title}</h3>
                  <p className="text-gray-600 text-sm">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Map or CTA Section */}
        <section className="py-20 bg-gray-900 relative overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#ff6b35] rounded-full filter blur-[128px] opacity-20"></div>
          </div>

          <div className="relative max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 reveal">
              Prefer a Direct Conversation?
            </h2>
            <p className="text-gray-400 text-lg mb-10 reveal" style={{ animationDelay: '0.1s' }}>
              Schedule a call with our team to discuss your project in detail.
            </p>
            <div className="flex flex-wrap justify-center gap-4 reveal" style={{ animationDelay: '0.2s' }}>
              <a 
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center gap-3 bg-gradient-to-r from-[#ff6b35] to-[#ff8f5c] text-white px-8 py-4 rounded-full font-semibold shadow-xl hover:shadow-2xl hover:scale-[1.02] transition-all duration-300"
                data-testid="contact-cta-call"
              >
                <Phone className="w-5 h-5" />
                Call Us Now
              </a>
              <a 
                href={`mailto:${companyInfo.email}`}
                className="inline-flex items-center gap-3 bg-white/10 text-white px-8 py-4 rounded-full font-semibold border border-white/20 hover:bg-white/20 transition-all duration-300"
                data-testid="contact-cta-email"
              >
                <Mail className="w-5 h-5" />
                Email Us
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default ContactPage;
